import { useCallback, useEffect, useState } from "react";
import { api, errMsg, fmt } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, CheckCircle2, XCircle, Pencil, Inbox, ListChecks, Ban, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SaleEditor, money } from "@/components/SaleEditor";
import { ModuleHeader, SaleCard } from "@/components/SaleCard";

const TABS = [
  { k: "pending", label: "Bekleyen Yeni Satışlar", icon: Inbox },
  { k: "approved", label: "Onaylanan / Tamamlanan", icon: ListChecks },
  { k: "rejected", label: "İptal Edilen", icon: Ban },
];
const btn = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors";

function ShortageDialog({ data, onClose, onForce }) {
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="acc-shortage-dialog">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><AlertTriangle size={18} className="text-amber-600" /> Stok Yetersiz</DialogTitle></DialogHeader>
        <div className="text-sm text-muted-foreground">Aşağıdaki kalemlerde depo stoğu fişteki miktardan az. Yine de onaylarsanız stok eksiye düşer.</div>
        <div className="space-y-1.5">
          {(data?.shortages || []).map((s, i) => (
            <div key={i} className="rounded-lg bg-amber-500/10 px-3 py-2 text-sm flex justify-between gap-2" data-testid={`acc-shortage-${i}`}>
              <span>{s.source_name} · {s.breed_label} {s.label}</span>
              <span className="tabular-nums">İstenen {fmt(s.requested)} / Mevcut <b>{fmt(s.available)}</b></span>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className={`${btn} bg-secondary`} data-testid="acc-shortage-cancel">Vazgeç</button>
          <button onClick={onForce} className={`${btn} bg-amber-600 text-white hover:bg-amber-700`} data-testid="acc-shortage-force">Yine de Onayla ve Stoktan Düş</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ sale, onClose, onDone }) {
  const [reason, setReason] = useState("");
  useEffect(() => { setReason(""); }, [sale]);
  const go = async () => {
    try { await api.post(`/sales/${sale.id}/reject`, { reason }); toast.success("Fiş iptal edildi (stoktan düşülmedi)"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!sale} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="acc-reject-dialog">
        <DialogHeader><DialogTitle className="font-head">Fişi İptal Et / Reddet</DialogTitle></DialogHeader>
        <div className="text-sm text-muted-foreground">{sale?.customer_name} · {money(sale?.total_amount)}</div>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Sebep (isteğe bağlı)" data-testid="acc-reject-reason"
          className="min-h-[80px] w-full rounded-lg border border-input bg-background p-3 text-sm focus:ring-2 focus:ring-primary outline-none" />
        <DialogFooter className="gap-2">
          <button onClick={onClose} className={`${btn} bg-secondary`}>Vazgeç</button>
          <button onClick={go} className={`${btn} bg-destructive text-destructive-foreground hover:opacity-90`} data-testid="acc-reject-confirm">İptal Et</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Accounting({ onBack }) {
  const [tab, setTab] = useState("pending");
  const [sales, setSales] = useState([]);
  const [shortage, setShortage] = useState(null);
  const [reject, setReject] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { api.get("/sales").then((r) => setSales(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const approve = async (s, force = false) => {
    try {
      await api.post(`/sales/${s.id}/approve`, null, { params: force ? { force: true } : {} });
      toast.success(s.items.every((i) => i.source === "ekstra") ? "Fiş onaylandı" : "Fiş onaylandı, stoktan düşüldü");
      setShortage(null); load();
    } catch (e) {
      if (e?.response?.status === 409) setShortage({ sale: s, shortages: e.response.data?.detail?.shortages || [] });
      else toast.error(errMsg(e));
    }
  };
  const save = async (body) => { await api.put(`/sales/${edit.id}`, body); toast.success("Fiş güncellendi"); setEdit(null); load(); };
  const list = sales.filter((s) => s.status === tab);
  const count = (k) => sales.filter((s) => s.status === k).length;

  return (
    <div className="min-h-screen bg-background" data-testid="accounting-module">
      <ModuleHeader icon={Calculator} title="Ön Muhasebe — Onay / Takip" onBack={onBack} testid="acc" />
      <div className="p-4 max-w-5xl mx-auto">
        <div className="flex flex-wrap gap-2 mb-5">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`acc-tab-${t.k}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              <t.icon size={15} /> {t.label} <span className="text-[11px] opacity-80" data-testid={`acc-count-${t.k}`}>({count(t.k)})</span>
            </button>
          ))}
        </div>
        {!list.length ? <div className="text-sm text-muted-foreground p-6 text-center rounded-xl border border-dashed border-border" data-testid="acc-empty">Bu listede fiş yok.</div> : (
          <div className="space-y-4">
            {list.map((s, i) => (
              <SaleCard key={s.id} sale={s} testid={`acc-sale-${i}`}>
                {s.status === "pending" && (
                  <>
                    <button onClick={() => setEdit(s)} data-testid={`acc-edit-${i}`} className={`${btn} bg-secondary hover:bg-secondary/70`}><Pencil size={15} /> Düzenle</button>
                    <button onClick={() => setReject(s)} data-testid={`acc-reject-${i}`} className={`${btn} bg-destructive/10 text-destructive hover:bg-destructive/20`}><XCircle size={15} /> İptal / Reddet</button>
                    <button onClick={() => approve(s)} data-testid={`acc-approve-${i}`} className={`${btn} bg-green-600 text-white hover:bg-green-700`}><CheckCircle2 size={15} /> Onayla ve Stoktan Düş</button>
                  </>
                )}
              </SaleCard>
            ))}
          </div>
        )}
      </div>
      <ShortageDialog data={shortage} onClose={() => setShortage(null)} onForce={() => approve(shortage.sale, true)} />
      <RejectDialog sale={reject} onClose={() => setReject(null)} onDone={() => { setReject(null); load(); }} />
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="acc-edit-dialog">
          <DialogHeader><DialogTitle className="font-head">Fişi Düzenle</DialogTitle></DialogHeader>
          {edit && <SaleEditor initial={edit} submitLabel="Değişiklikleri Kaydet" onSubmit={save} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
